import LoadingIndicator from '@storeblocks/loading-indicator';
import { LayoutContextProvider } from 'App/LayoutContextProvider';
import { BrandContextProvider } from 'App/themes/BrandContext';
import { ColorTheme } from 'App/themes/ColorTheme/ColorTheme';
import { AppFooter, AppNavbar } from 'App/themes/PartnerTheme';
import { getPartnerBrandFromBaseUrl } from 'App/themes/themes.helpers';
import { Brand } from 'App/themes/themes.types';
import { AkaConsentModal } from 'Components/AkaConsentModal';
import ErrorBoundary from 'Components/ErrorBoundary/ErrorBoundary';
import { NoMatch } from 'Components/NoMatch';
import { PageLayout } from 'Components/PageLayout';
import { CancelInsurancePage } from 'Pages/CancelInsurancePage';
import ChangeAddressPage from 'Pages/ChangeAddressPage';
import ErgoHealthInsurancePage from 'Pages/ErgoHealthInsurancePage';
import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';
import { getBasePath } from 'Utils/navigation.utils';
import { RouteName } from './route.types';
import ScrollRestorationWrapper from './ScrollRestorationWrapper';

const ChangeFormsPage = lazy(() => import('Pages/ChangeFormsPage'));
const OverviewPage = lazy(() => import('Pages/OverviewPage'));
const InsuranceDetailsPage = lazy(() => import('Pages/InsuranceDetailsPage'));
const CorporateInsuranceDetailsPage = lazy(
  () => import('Pages/CorporateInsuranceDetailsPage'),
);
const InsuranceLimitedDetailsPage = lazy(
  () => import('Pages/InsuranceLimitedDetailsPage'),
);
const InvoiceArchivePage = lazy(() => import('Pages/InvoiceArchivePage'));
const VehicleLookupPage = lazy(() => import('Pages/VehicleLookup'));
const DocumentArchivePage = lazy(() => import('Pages/DocumentArchivePage'));
const ContactUsPage = lazy(() => import('Pages/ContactUsPage'));
const StartReregistrationPage = lazy(
  () => import('Pages/ReregistrationPage/StartReregistrationPage'),
);
const CompleteReregistrationPage = lazy(
  () => import('Pages/ReregistrationPage/CompleteReregistrationPage'),
);

export const AppRoutes = (): React.ReactElement => {
  const currentBasename = getBasePath();
  const brand = getPartnerBrandFromBaseUrl(currentBasename);

  return (
    <BrowserRouter basename={`/${currentBasename}`}>
      <ScrollRestorationWrapper>
        <LayoutContextProvider>
          <AppNavbar />
          <PageLayout>
            <BrandContextProvider initialBrand={brand}>
              {brand === Brand.AKADEMIKERNE && <AkaConsentModal />}
              <ColorTheme brand={brand} />
              <ErrorBoundary>
                <Suspense fallback={<LoadingIndicator />}>
                  <Routes>
                    <Route index element={<OverviewPage />} />

                    <Route
                      path={`${RouteName.INSURANCE_DETAILS}/:policyNumber`}
                      element={<InsuranceDetailsPage />}
                    />
                    <Route
                      path={`${RouteName.INSURANCE_DETAILS}/:policyNumber/endre`}
                      element={<ChangeFormsPage />}
                    />

                    <Route
                      path={RouteName.INVOICE_ARCHIVE}
                      element={<InvoiceArchivePage />}
                    />
                    <Route
                      path={RouteName.DOCUMENT_ARCHIVE}
                      element={<DocumentArchivePage />}
                    />
                    <Route
                      path={RouteName.CONTACT_US}
                      element={<ContactUsPage />}
                    />
                    <Route
                      path="/helseforsikring"
                      element={<ErgoHealthInsurancePage />}
                    />
                    <Route
                      path={`${RouteName.CORPORATE_INSURANCE_DETAILS}/:insuranceType`}
                      element={<CorporateInsuranceDetailsPage />}
                    />
                    <Route
                      path={`${RouteName.CANCEL_INSURANCE}/:policyNumber`}
                      element={<CancelInsurancePage />}
                    />
                    <Route
                      path={`${RouteName.CHANGE_ADDRESS}/:policyNumber`}
                      element={<ChangeAddressPage />}
                    />
                    <Route
                      path={`${RouteName.INSURANCE_LIMITED_DETAILS}/:policyNumber`}
                      element={<InsuranceLimitedDetailsPage />}
                    />
                    <Route
                      path={`${RouteName.RE_REGISTER}/:policyNumber/start`}
                      element={<StartReregistrationPage />}
                    />
                    <Route
                      path={`${RouteName.RE_REGISTER}/:policyNumber/fullfor`}
                      element={<CompleteReregistrationPage />}
                    />
                    <Route
                      path={`${RouteName.VEHICLE_LOOKUP}/:licensePlateNumber`}
                      element={<VehicleLookupPage />}
                    />
                    <Route path="*" element={<NoMatch />} />
                  </Routes>
                </Suspense>
              </ErrorBoundary>
            </BrandContextProvider>
          </PageLayout>
          <AppFooter />
        </LayoutContextProvider>
      </ScrollRestorationWrapper>
    </BrowserRouter>
  );
};
